import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CommunityVideo } from '../../../api/community/types';
import './CommunityActionMenu.css';

interface CommunityActionMenuProps {
  video: CommunityVideo;
  onEdit?: (video: CommunityVideo) => void;
  onUnpublish?: (video: CommunityVideo) => void;
  onPublish?: (video: CommunityVideo) => void;
  onDelete?: (video: CommunityVideo) => void;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}

const CommunityActionMenu: React.FC<CommunityActionMenuProps> = ({
  video,
  onEdit,
  onUnpublish,
  onPublish,
  onDelete,
  isOpen,
  onToggle,
  onClose
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  // Calculate menu position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const updatePosition = () => {
        if (buttonRef.current) {
          const buttonRect = buttonRef.current.getBoundingClientRect();
          const menuWidth = 150; // Approximate menu width
          setMenuPosition({
            top: buttonRect.bottom + 4,
            left: buttonRect.right - menuWidth // Align to right edge of button
          });
        }
      };
      
      updatePosition();
      
      // Update position on scroll/resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleEdit = () => {
    onClose();
    onEdit?.(video);
  };

  const handleUnpublish = () => {
    onClose();
    onUnpublish?.(video);
  };

  const handlePublish = () => {
    onClose();
    onPublish?.(video);
  };

  const handleDelete = () => {
    onClose();
    onDelete?.(video);
  };

  return (
    <div className="community-action-menu-wrapper">
      <button
        ref={buttonRef}
        className="community-action-menu-button"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        aria-label="More options"
      >
        <i className="ph-dots-three-vertical"></i>
      </button>
      {isOpen && createPortal(
        <div 
          ref={menuRef} 
          className="community-action-menu-dropdown community-action-menu-dropdown-portal"
          style={{
            position: 'fixed',
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
            zIndex: 99999,
            minWidth: '150px',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            padding: '4px',
            display: 'block',
            visibility: 'visible',
            opacity: 1
          }}
        >
          {onEdit && (
            <button onClick={handleEdit} className="menu-item">
              <i className="ph-pencil"></i>
              <span>Edit</span>
            </button>
          )}
          {onPublish && (
            <button onClick={handlePublish} className="menu-item">
              <i className="ph-eye"></i>
              <span>Publish</span>
            </button>
          )}
          {onUnpublish && (
            <button onClick={handleUnpublish} className="menu-item">
              <i className="ph-eye-slash"></i>
              <span>Unpublish</span>
            </button>
          )}
          {onDelete && (
            <button onClick={handleDelete} className="menu-item delete-option">
              <i className="ph-trash"></i>
              <span>Delete</span>
            </button>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default CommunityActionMenu;

